/**
 * BoekingDetail - Detail weergave van een boeking
 * GEKOPIEERD van Morris boeking_detail.html
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import BoekhoudingLayout from '../../components/boekhouding/BoekhoudingLayout';
import styles from '../../styles/Boekhouding.module.css';
import { boekhoudingService, Boeking } from '../../services/boekhouding.service';
import { getApiBaseUrl } from '../../services/api';

// Interface voor betalingen (zoals Morris)
interface Betaling {
  betaling_id: number;
  datum: string;
  boekstuk: string;
  bedrag: number;
}

// Interface voor gekoppelde factuur (zoals Morris)
interface GekoppeldeFactuur {
  id: number;
  boekstuk: string;
  boekstuknummer: string;
  datum: string;
  omschrijving: string;
  bedrag_incl: number;
  saldo: number;
}

const BoekingDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [boeking, setBoeking] = useState<Boeking | null>(null);
  const [betalingen] = useState<Betaling[]>([]);
  const [gekoppeldeFactuur, setGekoppeldeFactuur] = useState<GekoppeldeFactuur | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const data = await boekhoudingService.getBoeking(parseInt(id));
        // Morris: API geeft saldo en betaald mee (berekend uit betalingen tabel)
        setBoeking(data);

        // Laad gekoppelde factuur als gekoppeld_aan_id bestaat
        if (data.gekoppeld_aan_id) {
          try {
            const factuur = await boekhoudingService.getBoeking(data.gekoppeld_aan_id);
            // Morris: API geeft saldo mee (berekend uit betalingen tabel)
            setGekoppeldeFactuur({
              id: factuur.id,
              boekstuk: factuur.boekstuk,
              boekstuknummer: factuur.boekstuknummer || String(factuur.id),
              datum: factuur.datum,
              omschrijving: factuur.relatie_naam || factuur.omschrijving,
              bedrag_incl: factuur.bedrag_incl,
              saldo: factuur.saldo ?? factuur.bedrag_incl,
            });
          } catch (e) {
            console.error('Error loading gekoppelde factuur:', e);
          }
        }

        // Laad document met auth token (Buro42 aanpak - zie OnderdeelDocumentenList.tsx)
        if (data.heeft_document) {
          try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${getApiBaseUrl()}/api/v1/boekhouding/boekingen/${data.id}/document`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            if (response.ok) {
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              setDocumentUrl(url);
            }
          } catch (e) {
            console.error('Error loading document:', e);
          }
        }
      } catch (err) {
        console.error('Error loading boeking:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();

    // Cleanup blob URL bij unmount
    return () => {
      if (documentUrl) {
        window.URL.revokeObjectURL(documentUrl);
      }
    };
  }, [id]);

  // Morris filters
  const formatBedrag = (bedrag: number): string => {
    return new Intl.NumberFormat('nl-NL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(bedrag) || 0);
  };

  const formatDatum = (datum: string): string => {
    return new Date(datum).toLocaleDateString('nl-NL');
  };

  const handleDelete = async () => {
    if (!boeking || !window.confirm('Weet je zeker dat je deze boeking wilt verwijderen?')) {
      return;
    }
    setDeleting(true);
    try {
      await boekhoudingService.deleteBoeking(boeking.id);
      // De boekingenlijst zit op /boekhouding zelf; /boekhouding/boekingen
      // bestaat niet als route en liep dood. Hersteld 25-10-2026.
      navigate(`/boekhouding?boekstuk=${boeking.boekstuk}`);
    } catch (err) {
      console.error('Error deleting boeking:', err);
      alert('Kon boeking niet verwijderen');
    } finally {
      setDeleting(false);
    }
  };

  // Morris: {% block header_actions %}
  const headerActions = boeking ? (
    <>
      {/* <a href="{{ root_path }}/boeking/{{ boeking.id }}/bewerk" class="btn btn-primary">Bewerken</a> */}
      <Link to={`/boekhouding/boeking/${boeking.id}/bewerk`} className={`${styles.btn} ${styles.btnPrimary}`}>
        Bewerken
      </Link>

      {/* {% if boeking.boekstuk in ['inkoop', 'verkoop'] and boeking.saldo > 0.01 and boeking.status != 'afgekeurd' %} */}
      {(boeking.boekstuk === 'inkoop' || boeking.boekstuk === 'verkoop') &&
       (boeking.saldo || 0) > 0.01 &&
       boeking.status !== 'afgekeurd' && (
        <button
          className={`${styles.btn} ${styles.btnWarning}`}
          onClick={() => window.confirm('Factuur markeren als afgekeurd? Dit sluit het openstaand bedrag af.') && console.log('TODO: afkeuren')}
        >
          Afgekeurd
        </button>
      )}

      {/* <form action="{{ root_path }}/boeking/{{ boeking.id }}/verwijder" method="POST" ... */}
      <button
        onClick={handleDelete}
        disabled={deleting}
        className={`${styles.btn} ${styles.btnDanger}`}
      >
        {deleting ? 'Verwijderen...' : 'Verwijderen'}
      </button>

      {/* <a href="{{ root_path }}/boekingen?boekstuk={{ boeking.boekstuk }}" class="btn btn-secondary">Terug naar overzicht</a> */}
      <Link to={`/boekhouding?boekstuk=${boeking.boekstuk}`} className={`${styles.btn} ${styles.btnSecondary}`}>
        Terug naar overzicht
      </Link>
    </>
  ) : null;

  if (loading) {
    return (
      <BoekhoudingLayout title="Boeking Details">
        <div className={styles.loading}>Laden...</div>
      </BoekhoudingLayout>
    );
  }

  if (!boeking) {
    return (
      <BoekhoudingLayout title="Boeking Details">
        <div className={styles.error}>Boeking niet gevonden</div>
      </BoekhoudingLayout>
    );
  }

  return (
    <BoekhoudingLayout
      title={`Boeking Details ${boeking.boekstuknummer || boeking.id}`}
      headerActions={headerActions}
    >
      {/* Morris: {% block content %} */}
      {/* <div class="dashboard-grid"> */}
      <div className={styles.dashboardGrid}>

        {/* <div class="card"> */}
        <div className={styles.card}>
          {/* <h3>{{ boeking.boekstuk|title }} Boeking</h3> */}
          <h3>{boeking.boekstuk.charAt(0).toUpperCase() + boeking.boekstuk.slice(1)} Boeking</h3>

          {/* <div class="info-list"> */}
          <div className={styles.infoList}>

            {/* {% if boeking.boekstuk == 'inkoop' and boeking.extern_factuurnummer %} */}
            {boeking.boekstuk === 'inkoop' && boeking.extern_factuurnummer && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Factuurnummer:</span>
                <span className={styles.infoValue}>{boeking.extern_factuurnummer}</span>
              </div>
            )}

            {/* {% elif boeking.boekstuk == 'verkoop' %} */}
            {boeking.boekstuk === 'verkoop' && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Factuurnummer:</span>
                <span className={styles.infoValue}>{boeking.boekstuknummer}</span>
              </div>
            )}

            {/* <div class="info-item"><span class="info-label">Datum:</span>... */}
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Datum:</span>
              <span className={styles.infoValue}>{formatDatum(boeking.datum)}</span>
            </div>

            {/* <div class="info-item"><span class="info-label">{% if boeking.boekstuk == 'inkoop' %}Leverancier:{% elif... */}
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>
                {boeking.boekstuk === 'inkoop' ? 'Leverancier:' :
                 boeking.boekstuk === 'verkoop' ? 'Klant:' : 'Omschrijving:'}
              </span>
              <span className={styles.infoValue}>{boeking.relatie_naam || boeking.omschrijving}</span>
            </div>

            {/* <div class="info-item"><span class="info-label">Grootboek:</span>... */}
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Grootboek:</span>
              <span className={styles.infoValue}>{boeking.grootboeknaam}</span>
            </div>

            {/* {% if boeking.boekstuk in ['inkoop', 'verkoop'] %} */}
            {(boeking.boekstuk === 'inkoop' || boeking.boekstuk === 'verkoop') && (
              <>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Excl. BTW:</span>
                  <span className={styles.infoValue}>{formatBedrag(boeking.bedrag_excl)}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>BTW ({boeking.btw_percentage}%):</span>
                  <span className={styles.infoValue}>{formatBedrag(boeking.btw_bedrag)}</span>
                </div>
              </>
            )}

            {/* <div class="info-item info-total"> */}
            <div className={`${styles.infoItem} ${styles.infoTotal}`}>
              <span className={styles.infoLabel}>Totaal bedrag:</span>
              <span className={styles.infoValue}>{formatBedrag(boeking.bedrag_incl)}</span>
            </div>

          </div>
        </div>

        {/* {% if boeking.boekstuk in ['inkoop', 'verkoop'] %} */}
        {(boeking.boekstuk === 'inkoop' || boeking.boekstuk === 'verkoop') && (
          <div className={styles.card}>
            <h3>Betaalstatus</h3>
            <div className={styles.infoList}>

              {/* Status badge */}
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Status:</span>
                <span className={styles.infoValue}>
                  {/* {% if boeking.status == 'afgekeurd' %} */}
                  {boeking.status === 'afgekeurd' ? (
                    <span className={`${styles.badge} ${styles.badgeSecondary}`}>Afgekeurd</span>
                  ) : (boeking.saldo || 0) <= 0.01 ? (
                    /* {% elif boeking.saldo <= 0.01 %} */
                    <span className={`${styles.badge} ${styles.badgeSuccess}`}>Betaald</span>
                  ) : (
                    /* {% else %} */
                    <span className={`${styles.badge} ${styles.badgeWarning}`}>Openstaand</span>
                  )}
                </span>
              </div>

              {/* {% if boeking.status != 'afgekeurd' %} */}
              {boeking.status !== 'afgekeurd' && (
                <>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Betaald:</span>
                    <span className={styles.infoValue}>{formatBedrag(boeking.betaald || 0)}</span>
                  </div>
                  <div className={`${styles.infoItem} ${styles.infoTotal}`}>
                    <span className={styles.infoLabel}>Openstaand:</span>
                    <span className={`${styles.infoValue} ${(boeking.saldo || 0) > 0 ? styles.negative : ''}`}>
                      {formatBedrag(boeking.saldo || 0)}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* {% if betalingen and boeking.status != 'afgekeurd' %} */}
            {betalingen.length > 0 && boeking.status !== 'afgekeurd' && (
              <>
                <h3 style={{ marginTop: '1.5rem' }}>Betalingen</h3>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th>Via</th>
                      <th className={styles.alignRight}>Bedrag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* {% for bet in betalingen %} */}
                    {betalingen.map(bet => (
                      <tr
                        key={bet.betaling_id}
                        className={styles.clickable}
                        onClick={() => navigate(`/boekhouding/boeking/${bet.betaling_id}`)}
                      >
                        <td>{formatDatum(bet.datum)}</td>
                        <td>
                          <span className={`${styles.badge} ${styles[`badge${bet.boekstuk.charAt(0).toUpperCase() + bet.boekstuk.slice(1)}`]}`}>
                            {bet.boekstuk.charAt(0).toUpperCase() + bet.boekstuk.slice(1)}
                          </span>
                        </td>
                        <td className={styles.alignRight}>{formatBedrag(bet.bedrag)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* {% if boeking.saldo > 0.01 and boeking.status != 'afgekeurd' %} */}
            {(boeking.saldo || 0) > 0.01 && boeking.status !== 'afgekeurd' && (
              <div style={{ marginTop: '1rem' }}>
                <Link
                  to={`/boekhouding/boeking/nieuw?boekstuk=bank&factuur_id=${boeking.id}&type=${boeking.boekstuk}`}
                  className={`${styles.btn} ${styles.btnPrimary}`}
                >
                  Betaling toevoegen
                </Link>
              </div>
            )}
          </div>
        )}

        {/* {% if gekoppelde_factuur %} */}
        {gekoppeldeFactuur && (
          <div className={styles.card}>
            <h3>Gekoppeld aan {gekoppeldeFactuur.boekstuk.charAt(0).toUpperCase() + gekoppeldeFactuur.boekstuk.slice(1)}</h3>
            <div className={styles.infoList}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>
                  {gekoppeldeFactuur.boekstuk === 'verkoop' ? 'Factuurnr:' : 'Bkst.nr:'}
                </span>
                <span className={styles.infoValue}>{gekoppeldeFactuur.boekstuknummer}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Datum:</span>
                <span className={styles.infoValue}>{formatDatum(gekoppeldeFactuur.datum)}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Omschrijving:</span>
                <span className={styles.infoValue}>{gekoppeldeFactuur.omschrijving}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Bedrag:</span>
                <span className={styles.infoValue}>{formatBedrag(gekoppeldeFactuur.bedrag_incl)}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Status:</span>
                <span className={styles.infoValue}>
                  {gekoppeldeFactuur.saldo <= 0.01 ? (
                    <span className={`${styles.badge} ${styles.badgeSuccess}`}>Betaald</span>
                  ) : (
                    <span className={`${styles.badge} ${styles.badgeWarning}`}>Open ({formatBedrag(gekoppeldeFactuur.saldo)})</span>
                  )}
                </span>
              </div>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <Link to={`/boekhouding/boeking/${gekoppeldeFactuur.id}`} className={`${styles.btn} ${styles.btnSecondary}`}>
                Bekijk factuur
              </Link>
            </div>
          </div>
        )}

        {/* {% if boeking.notitie %} */}
        {boeking.notitie && (
          <div className={styles.card}>
            <h3>Notitie</h3>
            <p>{boeking.notitie}</p>
          </div>
        )}

      </div>

      {/* {% if heeft_document %} */}
      {boeking.heeft_document && documentUrl ? (
        <div className={styles.card} style={{ marginTop: '1.5rem' }}>
          <h3>Document</h3>
          <div className={styles.documentViewer}>
            {/* {% if document_type == '.pdf' %} */}
            {boeking.document_type === '.pdf' ? (
              <iframe
                src={documentUrl}
                style={{ width: '100%', height: '800px', border: '1px solid #ddd', borderRadius: '4px' }}
                title="Document"
              />
            ) : (
              <img
                src={documentUrl}
                alt="Factuur document"
                style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            )}
            <div style={{ marginTop: '1rem' }}>
              <a
                href={documentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`${styles.btn} ${styles.btnSecondary}`}
              >
                Openen in nieuw tabblad
              </a>
            </div>
          </div>
        </div>
      ) : (boeking.boekstuk === 'inkoop' || boeking.boekstuk === 'verkoop') && (
        /* {% elif boeking.boekstuk in ['inkoop', 'verkoop'] %} */
        <div className={styles.card} style={{ marginTop: '1.5rem' }}>
          <h3>Document</h3>
          <p style={{ color: '#888' }}>Geen document gevonden voor deze boeking.</p>
        </div>
      )}

    </BoekhoudingLayout>
  );
};

export default BoekingDetail;
